// src/components/IncidentTimeline.jsx — Permissions 42 Phase 3 Lot 5.
//
// Timeline ADMIN d'un incident (déclaration + chaque changement de statut :
// qui / quand). Dérivée d'audit_log via l'endpoint serveur `incident_timeline`
// (status only, 0 PII voyageur). LECTURE SEULE. Affiche aussi un badge
// « 🔁 requalifié par {orchestrateur} le {date} » dérivé de la même timeline.
//
// Chargement à la demande (lazy) : on ne fetch la timeline que si `incidentId`
// est fourni ET que le composant est monté (carte admin dépliée).

import { useState, useEffect } from "react"
import { DS } from "../lib/ui"
import { statusMeta, fetchIncidentTimeline, lastOrchestratorRequalif } from "../lib/incidents-api"

// Libellé court d'une entrée de timeline (qui a fait quoi).
function entryLabel(e) {
  if (e.action === "INSERT") {
    return `Déclaré${e.new_status ? ` (${statusMeta(e.new_status).label})` : ""}`
  }
  if (e.action === "DELETE") return "Supprimé"
  // UPDATE : changement de statut éventuel
  if (e.old_status && e.new_status && e.old_status !== e.new_status) {
    return `${statusMeta(e.old_status).label} → ${statusMeta(e.new_status).label}`
  }
  return "Modifié"
}

function roleLabel(role) {
  if (role === "orchestrateur") return "responsable"
  if (role === "menage") return "ménage"
  if (role === "admin") return "admin"
  return role || "—"
}

function fmtDateTime(iso) {
  if (!iso) return "—"
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit",
    })
  } catch (_) {
    return iso
  }
}

/**
 * Badge dérivé « requalifié par {orchestrateur} ». Rendu seul (sans la timeline
 * complète) — utilisable dans l'en-tête d'une carte admin.
 */
export function RequalifBadge({ timeline }) {
  const r = lastOrchestratorRequalif(timeline)
  if (!r) return null
  return (
    <span
      title={`Dernière requalification par ${r.by}`}
      style={{ background: "#ede9fe", color: "#6d28d9", borderRadius: 6, padding: "2px 8px", fontSize: 10, fontWeight: 700 }}
    >
      🔁 Requalifié par {r.by} le {fmtDateTime(r.at)}
    </span>
  )
}

/**
 * Timeline complète (lecture seule) + badge requalif intégré. Charge la timeline
 * à la demande. `onLoaded(timeline)` optionnel : remonte la timeline au parent
 * (ex. pour afficher RequalifBadge ailleurs sans re-fetch).
 */
export function IncidentTimeline({ incidentId, onLoaded }) {
  const [timeline, setTimeline] = useState([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (!incidentId) { setTimeline([]); setLoading(false); return }
    setLoading(true)
    ;(async () => {
      const t = await fetchIncidentTimeline(incidentId)
      if (cancelled) return
      setTimeline(t)
      setLoading(false)
      if (typeof onLoaded === "function") onLoaded(t)
    })()
    return () => { cancelled = true }
  }, [incidentId, onLoaded])

  if (loading) {
    return <div style={{ fontSize: 11, color: "#aaa", marginTop: 6 }}>⏳ Historique…</div>
  }
  if (!timeline.length) return null

  return (
    <div style={{ marginTop: 8 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: DS.navy, fontWeight: 700, padding: 0, fontFamily: DS.fontBody }}
      >
        {open ? "▾" : "▸"} Historique ({timeline.length})
      </button>
      {open && (
        <ol style={{ listStyle: "none", margin: "8px 0 0", padding: "10px 12px", background: "#f8fafd", borderRadius: 8, fontSize: 11, color: "#555", lineHeight: 1.6 }}>
          {timeline.map((e, i) => (
            <li key={i} style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: i < timeline.length - 1 ? 4 : 0 }}>
              <span style={{ color: "#888" }}>{fmtDateTime(e.created_at)}</span>
              <span style={{ fontWeight: 700, color: DS.navy }}>{entryLabel(e)}</span>
              <span style={{ color: "#888" }}>· {roleLabel(e.user_role)}{e.user_email ? ` (${e.user_email})` : ""}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
